/**
 * Admin: Beta Feedback Dashboard
 * Route: /admin/feedback
 *
 * Lists all survey responses with quick stats and a one-click CSV export.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Download, MessageCircle, ChevronDown } from 'lucide-react';
import api from '../lib/api';

const FEATURE_LABELS = {
  plans: 'Plans', stories: 'Stories', chat: 'Chat', explore: 'Explore',
  profiles: 'Profiles', reputation_levels: 'Reputation & Levels', other: 'Other',
};

const USE_LABELS = {
  definitely_not: 'Definitely not', probably_not: 'Probably not',
  maybe: 'Maybe', probably_yes: 'Probably yes', definitely_yes: 'Definitely yes',
};

const ACTION_LABELS = {
  create_plan: 'Create plan', join_plan: 'Join plan', post_story: 'Post story',
  send_message: 'Send msg', follow_someone: 'Follow', explore_plans: 'Explore',
  none: 'None',
};

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get('/admin/feedback/beta/stats'),
          api.get('/admin/feedback/beta?limit=200'),
        ]);
        setStats(s.data);
        setItems(l.data.items || []);
      } catch (e) {
        // 403 = not an admin → bounce to home. Any other error stays in-page.
        if (e?.response?.status === 403 || e?.response?.status === 401) {
          navigate('/home');
          return;
        }
        setLoadError(e?.response?.data?.detail || 'Could not load feedback. Try again.');
      }
      setLoading(false);
    })();
  }, [navigate]);

  const downloadCsv = async () => {
    // Use fetch + blob so the streaming response is preserved (axios JSON parsing
    // would corrupt the CSV). Token must match the key used by src/lib/api.js.
    const token = localStorage.getItem('asap_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${base}/api/admin/feedback/beta/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `asap_beta_feedback_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F2] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#6B6E73]" />
      </div>
    );
  }

  const empty = !stats || stats.total === 0;

  return (
    <div className="min-h-screen bg-[#F8F7F2]" data-testid="admin-feedback-page">
      <header className="sticky top-0 bg-[#F8F7F2]/95 backdrop-blur z-10 border-b border-black/[0.04] pt-safe-area">
        <div className="flex items-center gap-2 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 active:scale-95" aria-label="Back" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-[#111]">Beta Feedback</h1>
            <p className="text-[11px] text-[#9B9EA3]">{stats?.total ?? 0} responses</p>
          </div>
          {!empty && (
            <button
              onClick={downloadCsv}
              data-testid="download-csv"
              className="flex items-center gap-1.5 bg-[#111] text-white text-xs font-bold px-3 py-2 rounded-full active:scale-95"
            >
              <Download size={13} /> CSV
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-4 py-5 space-y-5 pb-24">
        {loadError && (
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-2xl px-3 py-2.5 text-sm text-[#FF3B30] font-bold" data-testid="admin-load-error">
            {loadError}
          </div>
        )}
        {empty ? (
          <div className="bg-white rounded-2xl border border-black/[0.04] p-8 text-center">
            <MessageCircle size={28} className="mx-auto text-[#9B9EA3] mb-2" />
            <p className="text-sm font-bold text-[#111]">No responses yet</p>
            <p className="text-xs text-[#9B9EA3] mt-1">Share the survey link with testers:</p>
            <p className="text-xs font-mono bg-[#F1F0EA] rounded-lg px-2 py-1.5 mt-2 inline-block break-all">
              {window.location.origin}/feedback
            </p>
          </div>
        ) : (
          <>
            {/* Stats */}
            <section data-testid="feedback-stats">
              <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Snapshot</h2>
              <div className="grid grid-cols-3 gap-2">
                <Stat label="NPS" value={stats.nps_score} note={`P${stats.promoters} · D${stats.detractors}`} />
                <Stat label="Avg ease" value={stats.avg_ease.toFixed(1)} note="out of 5" />
                <Stat label="Avg NPS" value={stats.avg_nps.toFixed(1)} note="out of 10" />
                <Stat label="Bug reports" value={stats.had_bugs} note="users hit issues" />
                <Stat label="Follow-up" value={stats.follow_up_yes} note="said yes" />
                <Stat label="Responses" value={stats.total} note="total" />
              </div>

              {Object.keys(stats.favorite_feature_counts || {}).length > 0 && (
                <div className="bg-white rounded-2xl border border-black/[0.04] p-3 mt-3">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#9B9EA3] mb-1.5">Favorite features</p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(stats.favorite_feature_counts).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                      <span key={k} className="text-[11px] font-bold bg-[#B6E600]/20 text-[#3F5200] px-2 py-1 rounded-full">
                        {FEATURE_LABELS[k] || k}: {v}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {Object.keys(stats.would_use_again_counts || {}).length > 0 && (
                <div className="bg-white rounded-2xl border border-black/[0.04] p-3 mt-2">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#9B9EA3] mb-1.5">Would use again</p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(stats.would_use_again_counts).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                      <span key={k} className="text-[11px] font-bold bg-[#F1F0EA] text-[#111] px-2 py-1 rounded-full">
                        {USE_LABELS[k] || k}: {v}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Response list */}
            <section data-testid="feedback-list">
              <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Responses</h2>
              <div className="space-y-2">
                {items.map(it => {
                  const isOpen = expanded === it.id;
                  return (
                    <div key={it.id} data-testid={`response-${it.id}`} className="bg-white rounded-2xl border border-black/[0.04] overflow-hidden">
                      <button
                        onClick={() => setExpanded(isOpen ? null : it.id)}
                        className="w-full flex items-center gap-3 p-3 text-left active:bg-[#F1F0EA]"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-extrabold text-[#111] truncate">
                            {it.display_name || it.username || 'Anonymous'}
                          </p>
                          <p className="text-[11px] text-[#9B9EA3] truncate">
                            {it.city || '—'} · NPS {it.nps} · Ease {it.ease_of_understanding} · {new Date(it.submitted_at_ts || it.created_at).toLocaleString()}
                          </p>
                        </div>
                        {it.had_bugs && <span className="text-[10px] font-bold text-[#FF3B30] bg-[#FF3B30]/10 px-2 py-0.5 rounded-full">Bug</span>}
                        <ChevronDown size={16} className={`text-[#9B9EA3] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 space-y-2 text-sm border-t border-black/[0.04] pt-3" data-testid={`response-${it.id}-detail`}>
                          <Row label="What is ASAP for?" value={it.what_is_asap_for} />
                          <Row label="Completed" value={(it.actions_completed || []).map(a => ACTION_LABELS[a] || a).join(', ')} />
                          {it.most_confusing && <Row label="Most confusing" value={it.most_confusing} />}
                          {it.had_bugs && (
                            <>
                              <Row label="Bug" value={it.bug_description} />
                              {it.bug_screenshot_url && (
                                <a href={it.bug_screenshot_url} target="_blank" rel="noreferrer" className="block">
                                  <img loading="lazy" decoding="async" src={it.bug_screenshot_url} alt="Bug" className="rounded-xl max-h-48 border border-black/[0.06]" />
                                </a>
                              )}
                            </>
                          )}
                          <Row label="Favorite" value={`${FEATURE_LABELS[it.favorite_feature] || it.favorite_feature || '—'}${it.favorite_other ? ` (${it.favorite_other})` : ''}`} />
                          {it.remove_or_improve && <Row label="Remove/improve" value={it.remove_or_improve} />}
                          <Row label="Use again" value={USE_LABELS[it.would_use_again] || it.would_use_again} />
                          {it.final_thoughts && <Row label="Final thoughts" value={it.final_thoughts} />}
                          {it.follow_up_ok && <Row label="Follow-up" value={it.follow_up_contact || 'Yes (no contact given)'} />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, note }) {
  return (
    <div className="bg-white rounded-2xl border border-black/[0.04] p-3">
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#9B9EA3]">{label}</p>
      <p className="text-xl font-black text-[#111] tabular-nums mt-0.5">{value}</p>
      {note && <p className="text-[10px] text-[#9B9EA3] mt-0.5">{note}</p>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#9B9EA3]">{label}</p>
      <p className="text-sm text-[#111] whitespace-pre-wrap break-words">{value}</p>
    </div>
  );
}
